import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import StyledDataGrid from "../../../shared/StyledDataGrid";
import { Product } from "../../../interfaces/DTO/mislaka_xml_models";
import { MimshakService } from "../../../services/mimshakService";
import { MimshakGroupByService } from "../../../services/MimshakGroupByService";
import { formatCurrency } from "../../../utils/namesFormater";
import { formatPeriodLabel } from "../../../shared/UIParts/chartUtils";
import { formatProductType } from "../../../utils/enumFormatter";
import { colorProductType } from "../../../utils/colorEnumFormatter";
import { monthlyPeriodColumns } from "../../../shared/UIParts/columnsFormatter";

interface Props {
  products: Product[];
}

const DepositSummaryByProductTable: React.FC<Props> = ({ products }) => {
  const allPolicies = useMemo(() => products.flatMap(p => p.policies_or_accounts), [products]);
  const latestUpdateDate = useMemo(() => MimshakService.getLatestUpdateDate(allPolicies), [allPolicies]);

  const aggregated = useMemo(() => {
    if (!latestUpdateDate) return {};
    return MimshakGroupByService.groupByDepositsByPeriodAndProductTypeFillingEmptyLast12(products, latestUpdateDate);
  }, [products, latestUpdateDate]);

  const uniquePeriods = useMemo(() => Object.keys(aggregated).map(Number).sort((a, b) => a - b), [aggregated]);

  const productTypes = useMemo(() => {
    const typesSet = new Set<string>();
    Object.values(aggregated).forEach(month => {
      Object.keys(month).forEach(pt => typesSet.add(pt));
    });
    return Array.from(typesSet).sort();
  }, [aggregated]);

  const rows = useMemo(() => {
    return productTypes.map((productType, index) => {
      const row: any = {
        id: index,
        product_type: Number(productType),
        label: formatProductType(Number(productType)),
      };
      uniquePeriods.forEach(period => {
        row[period] = aggregated[period]?.[productType] || 0;
      });
      return row;
    });
  }, [productTypes, aggregated, uniquePeriods]);

  const totals = useMemo(() => {
    const totalRow: Record<string, number | string> = {
      id: "total",
      label: "סה״כ",
    };
    uniquePeriods.forEach(period => {
      const sum = productTypes.reduce((acc, pt) => acc + (aggregated[period]?.[pt] || 0), 0);
      totalRow[period] = sum;
    });
    return totalRow;
  }, [aggregated, uniquePeriods, productTypes]);

  const columns: GridColDef[] = useMemo(() => {
    const base: GridColDef[] = [
      {
        field: "label",
        headerName: "סוג מוצר",
        width: 180,
        renderCell: (params) => {
          const isTotal = params.row.id === "total";
          return (
            <Box
              sx={{
                backgroundColor: isTotal ? "#1976d2" : colorProductType(params.row.product_type),
                color: "white",
                px: 1.5,
                py: 0.5,
                borderRadius: 1,
                width: "100%",
                textAlign: "center",
                fontWeight: isTotal ? "bold" : "normal",
              }}
            >
              {params.value}
            </Box>
          );
        },
      },
    ];
     const monthCols = monthlyPeriodColumns(uniquePeriods)
    // const monthCols = uniquePeriods.map((period) => ({
    //   field: period.toString(),
    //   headerName: formatPeriodLabel(period.toString()),
    //   width: 120,
    //   align: "center" as const,
    //   headerAlign: "center" as const,
    //   renderCell: (params: any) => (
    //     <span dir="ltr" style={{ fontWeight: params.row.id === "total" ? "bold" : "normal" }}>
    //       {params.value > 0 ? formatCurrency(params.value) : ""}
    //     </span>
    //   ),
    // }));

    return [...base, ...monthCols];
  }, [uniquePeriods]);

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h6" gutterBottom>
        טבלת הפקדות לפי סוג מוצר (12 חודשים אחרונים)
      </Typography>
      <StyledDataGrid
        rows={[...rows, totals]}
        columns={columns}
        hideFooter
    
      />
    </Box>
  );
};

export default DepositSummaryByProductTable;
